import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon.tsx';
import './Banner.css';

export type BannerVariant = 'offline' | 'syncing' | 'info' | 'warning' | 'error';

const ICON: Record<Exclude<BannerVariant, 'syncing'>, IconName> = {
  offline: 'cloud-off',
  info: 'info',
  warning: 'alert',
  error: 'alert',
};

/**
 * DESIGN.md §10 Banner: inline under the TopNav. Offline is calm grey, never red; errors carry
 * a Retry action.
 */
export function Banner({
  variant,
  icon,
  children,
  action,
}: {
  variant: BannerVariant;
  icon?: IconName;
  children: ReactNode;
  /** A ghost action ("Retry"). */
  action?: { label: string; onClick: () => void };
}) {
  const role = variant === 'error' ? 'alert' : 'status';
  return (
    <div className={`banner banner--${variant}`} role={role}>
      <span className="banner__icon">
        {variant === 'syncing' ? (
          <span className="spinner" aria-hidden="true" />
        ) : (
          <Icon name={icon ?? ICON[variant]} />
        )}
      </span>
      <div className="banner__text">{children}</div>
      {action && (
        <button type="button" className="banner__action" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  );
}
